/**
 * «Uso de la cuenta»: la bolsa común de todos tus proyectos.
 *
 * Hay dos clases de tope y vivían revueltas en el «Uso» de cada proyecto:
 * - Los de cada SITIO —páginas, CMS, ofertas, contactos, asientos—, que se
 *   cuentan proyecto por proyecto. Siguen allí.
 * - Los de la PERSONA —cuántos proyectos, la IA, el almacenamiento, los
 *   formularios, el tráfico—, que el servidor cuenta contra quien paga, sume
 *   los sitios que sume. Son éstos. Enseñados dentro de un proyecto, los tres
 *   sitios de alguien pintaban la misma barra como si cada uno tuviera la suya.
 *
 * Va con Planes y Facturación, en el menú de la cuenta: la bolsa la pone la
 * membresía, y desde aquí es de donde se sube de plan.
 */
import { useEffect, useState } from 'react'
import { IconActivity, IconArrowRight, IconChevronDown, IconDeviceSdCard, IconLayoutGrid, IconMessage } from '@tabler/icons-react'
import { MascotIcon } from '../../lib/mascotIcon'
import { getAccountUsage, type AccountUsage as AccountUsageData, type Project } from '../../lib/api'
import { activeLanguage, useI18n } from '../../lib/i18n'
import { PROJECT_LIMITS } from '../../lib/planLimits'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { UsageStatCard, UsageStatSkeleton, fmtBytes, formatUsageReset, type UsageStat } from '../ProjectWorkspace/sections/UsageStatCard'

export function AccountUsage({ token, projects, onOpenPlans }: {
  token: string
  projects: Project[]
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  const [usage, setUsage] = useState<AccountUsageData | null>(null)
  const [showTrafficProjects, setShowTrafficProjects] = useState(false)
  const [nowMs, setNowMs] = useState(() => Date.now())

  useEffect(() => {
    let alive = true
    getAccountUsage(token).then((data) => { if (alive) setUsage(data) }).catch(() => {})
    return () => { alive = false }
  }, [token])
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [])
  // Refresh at the exact boundary so both the percentage and the next reset
  // move forward without requiring a reload.
  useEffect(() => {
    if (!usage?.aiTokensResetAt) return
    const delay = Math.max(0, new Date(usage.aiTokensResetAt).getTime() - Date.now() + 1_000)
    const timer = window.setTimeout(() => {
      void getAccountUsage(token).then(setUsage).catch(() => {})
      setNowMs(Date.now())
    }, Math.min(delay, 2_147_483_647))
    return () => window.clearTimeout(timer)
  }, [token, usage?.aiTokensResetAt])

  const plan = usage?.plan ?? 'free'
  /* Los compartidos contigo no cuentan: el cupo es de los que pagas tú. La
     misma cuenta que el aviso de «llegaste al tope» al crear uno. */
  const ownedProjects = projects.filter((project) => !project.shared).length

  const aiStats: UsageStat[] = usage ? [
    // Como porcentaje: el recuento de tokens no le dice nada a quien nunca ha
    // visto un tokenizador.
    {
      id: 'ai',
      label: t('usage.stat.ai'),
      icon: MascotIcon,
      description: t('usage.stat.aiBody'),
      resetDescription: formatUsageReset(usage.aiTokensResetAt, t, nowMs),
      used: usage.used.aiTokens,
      limit: usage.limits.ai_tokens,
      usedLabel: usage.limits.ai_tokens ? `${Math.min(100, Math.round((usage.used.aiTokens / usage.limits.ai_tokens) * 100))}%` : t('common.none'),
      limitLabel: usage.limits.ai_tokens ? '100%' : t('usage.noLimit'),
    },
    {
      id: 'generations',
      label: t('usage.stat.generations'),
      icon: MascotIcon,
      description: t('usage.stat.generationsBody'),
      /* En el plan gratis son de por vida: no hay fecha que prometer. */
      resetDescription: usage.aiCreditsResetAt ? formatUsageReset(usage.aiCreditsResetAt, t, nowMs) : t('usage.noReset'),
      used: usage.used.aiCredits,
      limit: usage.limits.ai_credits,
    },
  ] : []

  const resourceStats: UsageStat[] = usage ? [
    { id: 'projects', label: t('usage.stat.projects'), icon: IconLayoutGrid, description: t('usage.stat.projectsBody'), used: ownedProjects, limit: usage.projectLimit !== undefined ? usage.projectLimit : PROJECT_LIMITS[plan] },
    { id: 'storage', label: t('usage.stat.storage'), icon: IconDeviceSdCard, description: t('usage.stat.storageBody'), used: usage.used.storageBytes, limit: usage.limits.storage_bytes, usedLabel: fmtBytes(usage.used.storageBytes), limitLabel: fmtBytes(usage.limits.storage_bytes) },
    {
      id: 'forms',
      label: t('usage.stat.forms'),
      icon: IconMessage,
      description: t('usage.stat.formsBody'),
      resetDescription: usage.formSubmissionsResetAt ? formatUsageReset(usage.formSubmissionsResetAt, t, nowMs) : '',
      used: usage.used.formSubmissions,
      limit: usage.limits.form_submissions,
    },
    /* Sin tope: el tráfico dejó de venderse como cupo y aquí se enseña como lo
       que es, un dato del mes. `limit: null` apaga la barra y el porcentaje —
       enseñar «3 GB de 100 GB» volvería a prometer un tope que nadie aplica.
       Ver `USAGE_LIMITS` en el worker. */
    { id: 'bandwidth', label: t('usage.stat.bandwidth'), icon: IconActivity, description: t('usage.stat.bandwidthBody'), used: usage.used.bandwidthBytes, limit: null, usedLabel: fmtBytes(usage.used.bandwidthBytes), limitLabel: t('usage.noLimit') },
  ] : []

  const group = (title: string, stats: UsageStat[], skeletons: number) => (
    <div className="dash-site-usage-group">
      <h4>{title}</h4>
      <div className="dash-site-usage-grid dash-account-usage-grid">
        {usage
          ? stats.map((stat) => <UsageStatCard key={stat.id} stat={stat} plan={plan} onOpenPlans={onOpenPlans} action={stat.id === 'bandwidth' ? (
              <button type="button" className="dash-secondary-btn dash-traffic-toggle" aria-expanded={showTrafficProjects} aria-controls="account-traffic-projects" onClick={() => setShowTrafficProjects((open) => !open)}>
                {t('accountUsage.viewProjects')} <IconChevronDown size={14} style={{ transform: showTrafficProjects ? 'rotate(180deg)' : undefined }} />
              </button>
            ) : undefined} />)
          : Array.from({ length: skeletons }, (_, index) => <UsageStatSkeleton key={index} />)}
      </div>
    </div>
  )

  /* El mismo reparto que Facturación: título, explicación y acciones en la
     columna corta de la izquierda; las cifras, en la ancha de la derecha. */
  return (
    <div className="dash-dashboard-view dash-settings-view dash-account-usage-page">
      <div className="dash-settings-layout">
        <header className="dash-settings-intro dash-billing-intro">
          <DashboardPageHeader
            title={t('accountUsage.title')}
            subtitle={t('accountUsage.subtitle')}
            actions={(
              <button type="button" className="dash-primary-btn" onClick={onOpenPlans}>
                {t('plans.over.seePlans')} <IconArrowRight size={14} />
              </button>
            )}
          />
        </header>
        <div className="dash-settings-content">
          <section className="dash-settings-card dash-general-card dash-project-usage-limits dash-account-usage">
            {group(t('accountUsage.groupAi'), aiStats, 2)}
            {group(t('accountUsage.groupResources'), resourceStats, 4)}
            {showTrafficProjects ? (
              <section id="account-traffic-projects" className="dash-traffic-projects" aria-labelledby="account-traffic-title">
                <h3 id="account-traffic-title" className="dash-card-title">{t('accountUsage.trafficProjects')}</h3>
                <p className="dash-card-caption">{t('accountUsage.trafficEstimate')}</p>
                {usage?.bandwidthProjects?.length ? (
                  <div className="dash-traffic-table-scroll">
                    <table className="dash-traffic-table">
                      <thead><tr><th scope="col">{t('accountUsage.project')}</th><th scope="col">{t('traffic.views')}</th><th scope="col">{t('accountUsage.estimatedData')}</th></tr></thead>
                      <tbody>{usage.bandwidthProjects.map((project) => (
                        <tr key={project.projectId}>
                          <th scope="row">{project.name}</th>
                          <td>{project.views.toLocaleString(activeLanguage())}</td>
                          <td>{fmtBytes(project.bandwidthBytes)}</td>
                        </tr>
                      ))}</tbody>
                      <tfoot><tr><th scope="row">{t('accountUsage.total')}</th><td>{usage.bandwidthProjects.reduce((sum, project) => sum + project.views, 0).toLocaleString(activeLanguage())}</td><td>{fmtBytes(usage.used.bandwidthBytes)}</td></tr></tfoot>
                    </table>
                  </div>
                ) : <p className="dash-card-caption">{t(usage?.bandwidthProjects ? 'accountUsage.noProjects' : 'accountUsage.breakdownUnavailable')}</p>}
              </section>
            ) : null}
            <p className="dash-card-caption dash-account-usage-note">{t('accountUsage.perProject')}</p>
          </section>
        </div>
      </div>
    </div>
  )
}
